/**
 * Who a row is about, for a control whose visible word is the same on every row and for the
 * sentence the live region reads out.
 *
 * Falls back to the reason's own name where the wire named nobody — a pending invitation on an
 * address with no profile behind it yet. One definition, because the label a trainer hears when
 * they press "Gotowe" and the label they hear when it lands have to be the same words.
 *
 * Lifted out of `pages/trainer-home/lib/useQueueCard` on 18 September 2026, when `/zadania` became
 * the second surface answering the same queue: the label is a statement about an ITEM, and it was
 * living inside a hook that is about a bento card.
 */

import type { TFunction } from 'i18next';

import type { AttentionItem } from '../model/attentionItems';

export function taskLabel(item: AttentionItem, t: TFunction): string {
  return item.fullName === '' ? t(item.titleKey) : item.fullName;
}

/** How a task's primary is named to assistive technology, and what a pointer reads over it. */
export interface PrimaryNaming {
  /** The accessible name: the control's visible word, then who it is about. */
  readonly name: string;
  /** A tooltip, where the visible word alone does not say what the control leads to. */
  readonly hint: string | undefined;
}

/**
 * The primary's name on every surface that draws one — the Pulpit's row, the Przegląd's card foot
 * and the `/zadania` desk — so one act is never named two ways.
 *
 * One shape for every kind, the personal record's „Pogratuluj" included: the visible word, then
 * whom. It begins with the visible word, which is what a speech-control user says. The record had
 * its own key here until 12 October 2026; that key also named the control's VISIBLE word, which is
 * resolved with no values, so every surface drew „Pogratuluj: {{name}}". The word on the control
 * never carries a placeholder (`model/taskAction.test.ts` reads every one in both languages).
 *
 * @param label The word ON the control at this moment — a note toggle's changes while it is open.
 */
export function primaryNaming(item: AttentionItem, label: string, t: TFunction): PrimaryNaming {
  const task = taskLabel(item, t);
  return { name: t('web:home.action.forTask', { action: label, task }), hint: undefined };
}
